import { useState, useEffect } from "react";
import {
  Building2,
  Layers,
  Hash,
  MapPin,
  Tag,
} from "lucide-react";
import Swal from "sweetalert2";
import { GetBrand } from "../../../services/outletDetail";

interface BrandFormData {
  brandName: string;
  mallName: string;
  floor: string;
  unit: string;
  area: string;
  brandCategoryName: string;
  mainCategory: string;
  capacityLabel: string;
  status: number | string;
}

interface BrandInformationFormProps {
  tenantId: number | null;
  brandId: number | null;
}

const INITIAL_VALUES: BrandFormData = {
  brandName: "",
  mallName: "",
  floor: "",
  unit: "",
  area: "",
  brandCategoryName: "",
  mainCategory: "",
  capacityLabel: "",
  status: 1,
};

const inputClass =
  "w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg text-gray-900 bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-white cursor-not-allowed select-none";

const labelClass =
  "block text-sm font-medium text-gray-700 mb-2 dark:text-white/90";

const iconClass =
  "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4";

const sectionTitle =
  "text-base font-semibold text-gray-900 dark:text-gray-100 mb-6 pb-2 border-b border-gray-100 dark:border-gray-800";

export default function BrandInformationForm({ tenantId, brandId }: BrandInformationFormProps) {
  const [formData, setFormData] = useState<BrandFormData>(INITIAL_VALUES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || brandId === null || brandId === undefined) {  
      setFormData(INITIAL_VALUES);
      return;
    }

    const fetchBrandData = async () => {
      setLoading(true);
      try {
        const brandRes = await GetBrand(brandId, tenantId, 0, 0);

        if (brandRes?.Code === 0 && brandRes.brandDetailDatas.length > 0) {
          const b = brandRes.brandDetailDatas[0];
          setFormData({
            brandName: b.BrandName ?? "",
            mallName: b.MallName ?? "",
            floor: b.Floor ?? "",
            unit: b.Unit ?? "",
            area: b.Area ? String(b.Area) : "",
            brandCategoryName: b.BrandCategoryName ?? "",
            mainCategory: b.MainCategory ?? "",
            capacityLabel: b.CapacityLabel ?? "",
            status: b.Status ?? 1,
          });
        } else {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "error",
            title: brandRes?.Message || "Failed to fetch brand data",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });
        }
      } catch (error) {
        console.error("Error fetching brand data:", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "An error occurred while fetching brand data",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBrandData();
  }, [tenantId, brandId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
          Loading brand data...
        </p>
      </div>
    );
  }

  return (
    <div className="lg:p-6">
      <div className="max-w-5xl mx-auto p-6 sm:p-8">
        <div className="mb-8">
            <h2 className={sectionTitle}>Brand Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={labelClass}>Brand Name</label>
                <div className="relative">
                  <Building2 className={iconClass} />
                  <input
                    type="text"
                    name="brandName"
                    value={formData.brandName}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Mall Name</label>
                <div className="relative">
                  <Building2 className={iconClass} />
                  <input
                    type="text"
                    name="mallName"
                    value={formData.mallName}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={labelClass}>Main Category</label>
                <div className="relative">
                  <Layers className={iconClass} />
                  <input
                    type="text"
                    name="mainCategory"
                    value={formData.mainCategory}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Brand Category</label>
                <div className="relative">
                  <Tag className={iconClass} />
                  <input
                    type="text"
                    name="brandCategoryName"
                    value={formData.brandCategoryName}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={labelClass}>Floor</label>
                <div className="relative">
                  <Layers className={iconClass} />
                  <input
                    type="text"
                    name="floor"
                    value={formData.floor}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Unit</label>
                <div className="relative">
                  <Hash className={iconClass} />
                  <input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={labelClass}>Area (m²)</label>
                <div className="relative">
                  <MapPin className={iconClass} />
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    disabled
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}